export interface ShareCardPayload {
  title: string;
  subtitle?: string;
  body: string;
  source?: string;
  filename: string;
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
): number {
  const chars = Array.from(text.trim());
  const lines: string[] = [];
  let line = '';
  for (const char of chars) {
    const next = line + char;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = char;
      if (lines.length === maxLines) break;
    } else {
      line = next;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines && chars.length > lines.join('').length) {
    const last = lines.length - 1;
    let clipped = lines[last] ?? '';
    while (ctx.measureText(`${clipped}…`).width > maxWidth && clipped.length > 1) {
      clipped = clipped.slice(0, -1);
    }
    lines[last] = `${clipped}…`;
  }
  lines.forEach((item, index) => ctx.fillText(item, x, y + index * lineHeight));
  return y + lines.length * lineHeight;
}

/** 在本地生成可下载的 PNG 分享卡，不把用户内容上传到服务端。 */
export async function downloadShareCard(payload: ShareCardPayload, onReady?: (url: string) => void): Promise<boolean> {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  ctx.fillStyle = '#f4fbfd';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#063a52';
  ctx.fillRect(0, 0, canvas.width, 150);
  ctx.fillStyle = '#1aa8c9';
  ctx.fillRect(0, 150, canvas.width, 8);

  ctx.fillStyle = '#ffffff';
  ctx.font = '700 24px "Microsoft YaHei", "PingFang SC", sans-serif';
  ctx.fillText('科普趣答 · 鱼虾图鉴', 72, 64);
  ctx.font = '500 16px "Microsoft YaHei", "PingFang SC", sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,.78)';
  ctx.fillText('水产种质资源智能检索与问答', 72, 98);

  ctx.fillStyle = '#063a52';
  ctx.font = '700 42px "Microsoft YaHei", "PingFang SC", sans-serif';
  const titleEnd = drawWrappedText(ctx, payload.title, 72, 238, 1056, 54, 2);

  let y = titleEnd + 26;
  if (payload.subtitle) {
    ctx.fillStyle = '#1a8eaa';
    ctx.font = '600 20px "Microsoft YaHei", "PingFang SC", sans-serif';
    y = drawWrappedText(ctx, payload.subtitle, 72, y, 1056, 30, 2) + 18;
  }

  ctx.fillStyle = '#23485a';
  ctx.font = '400 23px "Microsoft YaHei", "PingFang SC", sans-serif';
  // Reserve the footer even when both the title and subtitle wrap.
  drawWrappedText(ctx, payload.body, 72, y, 1056, 38, Math.max(1, Math.min(5, Math.floor((510 - y) / 38) + 1)));

  if (payload.source) {
    ctx.fillStyle = '#557381';
    ctx.font = '500 17px "Microsoft YaHei", "PingFang SC", sans-serif';
    drawWrappedText(ctx, payload.source, 72, 548, 1056, 24, 1);
  }
  ctx.fillStyle = '#6d8791';
  ctx.font = '400 15px "Microsoft YaHei", "PingFang SC", sans-serif';
  ctx.fillText('打开链接查看完整资料 · 仅供科普参考', 72, 580);

  try {
    // `toBlob` 在部分内嵌浏览器中可能永远不触发回调，页面会因此既不下载也不提示。
    // Data URL 同步生成，能保持在用户点击的激活上下文内触发下载。
    const url = canvas.toDataURL('image/png');
    onReady?.(url);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = payload.filename.endsWith('.png') ? payload.filename : `${payload.filename}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return true;
  } catch {
    return false;
  }
}
